import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getIdToken, signOut } from '../../../services/cognitoService';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') ?? '';

/** Account tab — account deletion with verification flow */
const SettingsAccount: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);
  const [verificationCode, setVerificationCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  /** Request account deletion — sends verification code to email */
  const handleRequestDelete = async () => {
    setDeleteLoading(true);
    setDeleteError('');

    try {
      const token = getIdToken();
      if (!token) {
        setDeleteError(t('settings.deleteAccount.notLoggedIn', 'Please log in again'));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/user/account/delete-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error ?? 'Failed to send verification code');
      }

      setMaskedEmail(data.email_masked ?? '');
      setDeleteStep(2);
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : t('settings.deleteAccount.requestFailed', 'Failed to request deletion'),
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  /** Confirm account deletion with 6-digit verification code */
  const handleConfirmDelete = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setDeleteError(t('settings.deleteAccount.invalidCode', 'Please enter a valid 6-digit code'));
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');

    try {
      const token = getIdToken();
      if (!token) {
        setDeleteError(t('settings.deleteAccount.notLoggedIn', 'Please log in again'));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/user/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ confirmation_code: verificationCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error ?? 'Failed to delete account');
      }

      signOut();
      navigate('/', { replace: true });
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : t('settings.deleteAccount.deleteFailed', 'Failed to delete account'),
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  /** Close the delete confirmation modal and reset state */
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteStep(1);
    setVerificationCode('');
    setMaskedEmail('');
    setDeleteError('');
  };

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">{t('settings.account', 'Account')}</h2>

      {/* Danger Zone */}
      <div className="danger-zone-content">
        <div className="danger-zone-header">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Delete Account Icon</title>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <h3>{t('settings.deleteAccount.title', 'Delete Account')}</h3>
        </div>

        <p className="danger-zone-description">
          {t(
            'settings.deleteAccount.description',
            'Once you delete your account, there is no going back. All your data will be permanently removed, including your profile, conversations, and messages.',
          )}
        </p>

        <div className="danger-zone-warning">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Warning Icon</title>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            {t('settings.deleteAccount.warning', 'This action is permanent and cannot be undone.')}
          </span>
        </div>

        <button
          type="button"
          className="btn-delete-account"
          onClick={() => setShowDeleteModal(true)}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Trash Icon</title>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
          {t('settings.deleteAccount.button', 'Delete My Account')}
        </button>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay">
          <button
            type="button"
            className="delete-modal-backdrop"
            onClick={handleCloseDeleteModal}
            aria-label={t('common.closeModal', 'Close modal')}
          />
          <div className="delete-modal">
            <button
              type="button"
              className="delete-modal-close"
              onClick={handleCloseDeleteModal}
              aria-label={t('common.close', 'Close')}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>Close Icon</title>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {deleteStep === 1 ? (
              <>
                <div className="delete-modal-icon">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    aria-hidden="true"
                  >
                    <title>Warning Icon</title>
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>

                <h3 className="delete-modal-title">
                  {t('settings.deleteAccount.modalTitle', 'Delete your account?')}
                </h3>

                <p className="delete-modal-text">
                  {t(
                    'settings.deleteAccount.modalText',
                    'This will permanently delete your account and all associated data. A verification code will be sent to your email.',
                  )}
                </p>

                {deleteError && (
                  <div className="delete-modal-error">
                    <span>{deleteError}</span>
                  </div>
                )}

                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="btn-modal-cancel"
                    onClick={handleCloseDeleteModal}
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn-modal-confirm"
                    onClick={handleRequestDelete}
                    disabled={deleteLoading}
                  >
                    {deleteLoading && <span className="spinner" />}
                    {t('settings.deleteAccount.sendCode', 'Send code')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="delete-modal-icon">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    aria-hidden="true"
                  >
                    <title>Email Icon</title>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>

                <h3 className="delete-modal-title">
                  {t('settings.deleteAccount.verifyTitle', 'Enter verification code')}
                </h3>

                <p className="delete-modal-text">
                  {t('settings.deleteAccount.codeSent', 'We sent a 6-digit code to')}{' '}
                  <strong>{maskedEmail}</strong>
                </p>

                <div className="verification-code-input">
                  <input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    id="delete-verification-code"
                  />
                </div>

                <p className="delete-modal-hint">
                  {t('settings.deleteAccount.codeHint', 'Enter the 6-digit code from your email')}
                </p>

                {deleteError && (
                  <div className="delete-modal-error">
                    <span>{deleteError}</span>
                  </div>
                )}

                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="btn-modal-back"
                    onClick={() => {
                      setDeleteStep(1);
                      setVerificationCode('');
                      setDeleteError('');
                    }}
                  >
                    {t('common.back', 'Back')}
                  </button>
                  <button
                    type="button"
                    className="btn-modal-delete"
                    onClick={handleConfirmDelete}
                    disabled={deleteLoading || verificationCode.length !== 6}
                  >
                    {deleteLoading && <span className="spinner" />}
                    {t('settings.deleteAccount.confirmDelete', 'Delete Account')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsAccount;
